import type { ApiClient } from "../api/client";
import type { AdminReferralRow, BookScope } from "../api/types";

/**
 * The book as it was left: the query, every page loaded so far, and how far
 * down it the reader was. Opening a member unmounts this screen, and without
 * this the way back landed on page one, at the top, with the search cleared.
 * That is a nuisance at a desk and the whole workflow on a phone, where the
 * list is read by opening one row, going back, and opening the next.
 *
 * Module state rather than storage: it only has to outlive the member page,
 * not a reload, and a reload is the way to ask for fresh rows.
 *
 * It answers only the history entry it was taken on, in the session that
 * took it, for the operator who took it. Going back lands on that entry; the
 * Referrals tab or a link arrive on a new one, and a fresh page load (whose
 * first entry is always "default") is a new session with its own client.
 */
export interface BookSnapshot {
  entry: string;
  api: ApiClient;
  staffDid: string | null;
  search: string;
  scope: BookScope;
  rows: AdminReferralRow[];
  nextCursor: string | undefined;
  scrollY: number;
}

let snapshot: BookSnapshot | null = null;

/** The snapshot, if it belongs to this entry, session and operator. */
export function readBook(on: Pick<BookSnapshot, "entry" | "api" | "staffDid">): BookSnapshot | null {
  return snapshot &&
    snapshot.entry === on.entry &&
    snapshot.api === on.api &&
    snapshot.staffDid === on.staffDid
    ? snapshot
    : null;
}

/** Taken as the book unmounts; null when there is nothing worth coming back to. */
export function saveBook(next: BookSnapshot | null) {
  snapshot = next;
}

/**
 * Keeps a remembered row honest after a write made from the member page (a
 * role change), so the way back does not show the chip the write replaced.
 */
export function patchBookRow(userId: string, patch: Partial<AdminReferralRow>) {
  if (!snapshot) return;
  snapshot = {
    ...snapshot,
    rows: snapshot.rows.map((row) => (row.userId === userId ? { ...row, ...patch } : row)),
  };
}
